import Snippet from '@/components/ui/Snippet/Snippet';
import type { ISearchResultItem, ItemListAnalyticsModel, SearchRequest } from '@/@types/client-api';
import { StandaloneSegmentListHeader } from '@/components/ui/Snippet/SegmentList/Header';
import type { SegmentListHeaderProps } from '@/components/ui/Snippet/SegmentList/Header';

import useGetSearchResultRow from './useGetSearchResultRow';
import useGetSegmentList from './useGetSegmentList';
import { SearchModalPanelState } from './hooks/useSearchModalPanelState';
import { AnalyticsContext } from '@/analytics/constants';
import { SnippetButtonLabel } from './Snippets/buttonsLabels';
import { ReactNode } from 'react';

interface SearchResultRowProps {
  prevItem?: ISearchResultItem;
  resultItem: ISearchResultItem;
  isLoading: boolean;
  modalPanelState: SearchModalPanelState;
  shouldBeAllowedToAutoplayVideo?: boolean;
  index?: number;
  searchRequest?: SearchRequest;
  listId?: string;
  listName?: string;
  analyticsContext?: AnalyticsContext;
  itemListAnalytics?: ItemListAnalyticsModel;
  hideSeeMore?: boolean;
  onSecondaryButtonClick?: () => void | Promise<void>;
  secondaryButtonIsLoading?: boolean;
  secondaryButtonIsSaved?: boolean;
  additionalContent?: ReactNode;
  overrideActions?: ReactNode;
}

const SearchResultsRow = ({
  prevItem,
  resultItem,
  isLoading,
  modalPanelState,
  shouldBeAllowedToAutoplayVideo,
  index,
  searchRequest,
  listId,
  listName,
  analyticsContext,
  itemListAnalytics,
  hideSeeMore,
  onSecondaryButtonClick,
  secondaryButtonIsLoading,
  secondaryButtonIsSaved,
  additionalContent,
  overrideActions
}: SearchResultRowProps) => {
  const snippet = useGetSearchResultRow(
    resultItem,
    modalPanelState,
    listId,
    listName,
    analyticsContext,
    itemListAnalytics
  );
  const segmentList = useGetSegmentList({ resultItem, context: 'search' });
  const buttons = onSecondaryButtonClick
    ? snippet?.buttons?.map(button =>
        button.label === SnippetButtonLabel.saveToLibrary ||
        button.label === SnippetButtonLabel.savedToLibrary
          ? {
              ...button,
              ...(secondaryButtonIsSaved === undefined
                ? {}
                : {
                    label: secondaryButtonIsSaved
                      ? SnippetButtonLabel.savedToLibrary
                      : SnippetButtonLabel.saveToLibrary,
                    iconRight: secondaryButtonIsSaved ? ('check' as const) : ('add' as const)
                  }),
              onClick: onSecondaryButtonClick,
              actionStatus: secondaryButtonIsLoading ? ('loading' as const) : ('idle' as const)
            }
          : button
      )
    : snippet?.buttons;

  // Ranking score, for debugging purposes
  let score;
  if (resultItem.explanation && resultItem.score) {
    if (prevItem && prevItem.score) {
      const diff = resultItem.score - prevItem.score;
      const roundNum = 10 ** 4;
      const diffRounded = Math.round(diff * roundNum) / roundNum;
      score = `${resultItem.score} (${diffRounded})`;
    } else {
      score = String(resultItem.score);
    }
  }

  return (
    <>
      {!!snippet && (
        <Snippet
          {...snippet}
          content={
            additionalContent ? (
              <>
                {snippet.content}
                {additionalContent}
              </>
            ) : (
              snippet.content
            )
          }
          buttons={buttons}
          score={score}
          loading={isLoading}
          context="search"
          modalPanelState={modalPanelState}
          shouldBeAllowedToAutoplayVideo={shouldBeAllowedToAutoplayVideo}
          resultItem={resultItem}
          index={index}
          searchRequest={searchRequest}
          hideSeeMore={hideSeeMore}
          analyticsContext={analyticsContext}
          itemListAnalytics={itemListAnalytics}
          overrideActions={overrideActions}
        />
      )}
      {snippet?.variant === 'on-demand' && segmentList && (
        <StandaloneSegmentListHeader {...(segmentList as SegmentListHeaderProps)} />
      )}
    </>
  );
};

export default SearchResultsRow;
